// src/utils/logger.js
import { getAnalytics, logEvent } from "firebase/analytics";

// --- CONFIGURATION ---
// Fields that must NEVER leave the device in plain text/logs
const SENSITIVE_KEYS = [
  'score_anxiety', 'score_depression', 'score_trauma', 
  'score_substance', 'score_alcohol', 'score_gambling',
  'clinical_data', 'radar_data', 'answers', 'raw_json',
  'gad_7', 'phq_9', 'ptsd', 'score_focus', 'score_resilience'
];

/**
 * Recursively scrubs sensitive keys from an object
 * Returns a sanitized copy safe for logging/analytics
 */
const sanitize = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  if (Array.isArray(data)) {
    return data.map(item => sanitize(item));
  }

  const cleaned = {};
  Object.keys(data).forEach(key => {
    // 1. Check if key matches our blocklist
    const isSensitive = SENSITIVE_KEYS.some(badKey => 
      key.toLowerCase().includes(badKey.toLowerCase())
    );

    if (isSensitive) {
      cleaned[key] = '[REDACTED_CLINICAL_DATA]'; // 🛡️ COMPLIANCE SHIELD
    } else if (typeof data[key] === 'object') {
      cleaned[key] = sanitize(data[key]); // Recurse deeper
    } else {
      cleaned[key] = data[key];
    }
  });
  return cleaned;
};

export const SecureLogger = {
  /**
   * Log info messages (Safe for Dev, Filtered in Prod)
   */
  info: (message, data = null) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`ℹ️ ${message}`, data);
    }
  },

  /**
   * Log warnings with auto-sanitization
   */
  warn: (message, data = null) => {
    const cleanData = sanitize(data);
    console.warn(`⚠️ ${message}`, cleanData);
  },

  /**
   * Log critical errors to Console (and eventually Crashlytics)
   * This is the "firewall" that prevents PHI leaks during crashes.
   */
  error: (message, errorOrData) => {
    const cleanData = sanitize(errorOrData);
    console.error(`🚨 ${message}`, cleanData);
    // Future Crashlytics Hook:
    // crashlytics().log(`Error: ${message} - ${JSON.stringify(cleanData)}`);
  },
  
  /**
   * Safe Analytics Event
   * Ensures we don't accidentally attach clinical parameters to GA4 events
   */
  logEvent: (eventName, params = {}) => {
    const cleanParams = sanitize(params);
    const analytics = getAnalytics();
    logEvent(analytics, eventName, cleanParams);
  }
};